import React, { useEffect, useState } from 'react';
import { getAssignmentsForCourse, submitAssignment } from '../../lib/assess';
import type { Assignment } from '../../types/assess';
import { Stamp } from '../lms/ui';

const AssignmentPanel: React.FC<{ courseId: string; userId: string }> = ({ courseId, userId }) => {
  const [items, setItems] = useState<Assignment[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const load = async () => {
    setItems(await getAssignmentsForCourse(courseId, userId).catch(() => []));
  };

  // Reload when the course changes; load is intentionally scoped to that course.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [courseId]);

  const submit = async (a: Assignment) => {
    const text = (drafts[a.id] ?? a.submission?.content ?? '').trim();
    if (!text) {
      setMsg('Write your answer before submitting.');
      return;
    }
    setBusy(a.id);
    try {
      await submitAssignment(userId, a.id, text);
      await load();
      setMsg('Submission saved.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setBusy('');
    }
  };

  if (items.length === 0) {
    return (
      <section>
        <h3 className="text-[14px] font-semibold text-ink">Assignments</h3>
        <p className="mt-1 text-[12.5px] leading-5 text-ink-2">
          No assignments have been set for this course. Your instructor will add them here.
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-[14px] font-semibold text-ink">Assignments</h3>
        <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">
          {items.length} {items.length === 1 ? 'brief' : 'briefs'}
        </span>
      </div>

      {msg && (
        <div className="mt-3 border border-rule bg-paper px-3 py-2 text-[12px] leading-5 text-ink" role="status">
          {msg}
        </div>
      )}

      <div className="mt-4 divide-y divide-rule border-y border-rule">
        {items.map((a) => {
          const status = a.submission?.status;
          const graded = a.submission?.grade != null;
          return (
            <div key={a.id} className="py-5 first:pt-4 last:pb-1">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                <p className="min-w-0 text-[13.5px] font-semibold leading-5 text-ink">{a.title}</p>
                {status ? (
                  <Stamp tone={graded ? 'cleared' : 'pending'}>{graded ? `Graded ${a.submission?.grade}` : status}</Stamp>
                ) : (
                  <Stamp tone="neutral">Not submitted</Stamp>
                )}
              </div>
              {a.description && (
                <p className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-5 text-ink-2">{a.description}</p>
              )}

              {a.submission?.feedback && (
                <div className="mt-3 border-l-2 border-pine bg-pine-50 px-3 py-2">
                  <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">Instructor feedback</p>
                  <p className="mt-1 text-[12.5px] leading-5 text-ink">{a.submission.feedback}</p>
                </div>
              )}

              <label className="lms-label mt-4" htmlFor={`assignment-${a.id}`}>
                Your answer
              </label>
              <textarea
                id={`assignment-${a.id}`}
                value={drafts[a.id] ?? a.submission?.content ?? ''}
                onChange={(e) => setDrafts({ ...drafts, [a.id]: e.target.value })}
                rows={4}
                placeholder="Type your answer… (e.g. export plan, document checklist, market analysis)"
                className="lms-field resize-y leading-5"
              />
              <button
                onClick={() => void submit(a)}
                disabled={busy === a.id}
                className="lms-btn-primary mt-2.5 !py-2 !text-[12px]"
              >
                {busy === a.id ? 'Saving…' : a.submission ? 'Resubmit' : 'Submit assignment'}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AssignmentPanel;
